<template>
  <div class="post-composer">
    <!-- Collapsed State -->
    <div v-if="!isExpanded" class="composer-collapsed" @click="expandComposer">
      <img :src="userAvatar" alt="Your avatar" class="user-avatar" />
      <div class="placeholder-input">
        What's on your mind, {{ userData.name?.split(' ')[0] || 'Coach' }}?
      </div>
    </div>

    <!-- Expanded State -->
    <div v-else class="composer-expanded">
      <div class="composer-header">
        <img :src="userAvatar" alt="Your avatar" class="user-avatar" />
        <div class="user-info">
          <span class="user-name">{{ userData.name }}</span>
          <span class="user-role">{{ userData.userType }}</span>
        </div>
        <button class="close-btn" @click="collapseComposer">&times;</button>
      </div>

      <!-- Inline error / status feedback -->
      <div v-if="errorMessage" class="composer-error" role="alert">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="#d93025" aria-hidden="true">
          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
        </svg>
        <span>{{ errorMessage }}</span>
        <button class="composer-error__close" @click="errorMessage = ''" aria-label="Dismiss">&times;</button>
      </div>

      <div class="composer-body">
        <VoiceDictationButton v-model="content" label="Dictate post" />
        <textarea ref="contentInput" v-model="content"
          :placeholder="`What's on your mind, ${userData.name?.split(' ')[0] || 'Coach'}?`" class="content-input"
          @input="autoResize"></textarea>

        <!-- Attachments Preview -->
        <div v-if="attachments.length > 0" class="attachments-preview">
          <div v-for="(attachment, index) in attachments" :key="index" class="attachment-item">
            <div v-if="attachment.type === 'image'" class="preview-image">
              <img :src="attachment.preview" :alt="attachment.file.name" />
            </div>
            <div v-else-if="attachment.type === 'video'" class="preview-video">
              <video :src="attachment.preview" controls></video>
            </div>
            <div v-else class="preview-document">
              <div class="doc-icon">
                <svg viewBox="0 0 24 24" width="24" height="24" fill="#65676b">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
                </svg>
              </div>
              <span class="doc-name">{{ attachment.file.name }}</span>
            </div>
            <button class="remove-attachment" @click="removeAttachment(index)">
              &times;
            </button>
            <div v-if="attachment.uploading" class="upload-progress">
              <div class="progress-bar" :style="{ width: attachment.progress + '%' }"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="composer-footer">
        <div class="add-to-post">
          <span>Add to your post</span>
          <div class="attachment-buttons">
            <button class="attach-btn photo-btn" title="Add Photo" @click="triggerFileInput('image')">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="#45bd62">
                <path
                  d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm0 16H5V5h14v14zm-5-7l-3 4H7l3-4 2.5 3 3.5-4.5 4 5.5H7.5z" />
              </svg>
              <span class="btn-label">Photo</span>
            </button>
            <button class="attach-btn video-btn" title="Add Video" @click="triggerFileInput('video')">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="#f3425f">
                <path
                  d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4z" />
              </svg>
              <span class="btn-label">Video</span>
            </button>
            <button class="attach-btn doc-btn" title="Add Document" @click="triggerFileInput('document')">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="#1877f2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
              </svg>
              <span class="btn-label">Document</span>
            </button>
          </div>
        </div>

        <button class="post-btn" :disabled="!canPost || isSubmitting" @click="submitPost">
          {{ submitLabel }}
        </button>
      </div>

      <!-- Retention notice -->
      <p class="retention-notice">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="#65676b" aria-hidden="true">
          <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
        </svg>
        <span>Posts are automatically removed after 60 days. Pin a post to keep it permanently.</span>
      </p>

      <!-- Hidden file inputs -->
      <input ref="imageInput" type="file" accept="image/*" multiple hidden
        @change="handleFileSelect($event, 'image')" />
      <input ref="videoInput" type="file"
        accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-ms-wmv,.mp4,.mov,.avi,.webm,.wmv" hidden
        @change="handleFileSelect($event, 'video')" />
      <input ref="documentInput" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" multiple hidden
        @change="handleFileSelect($event, 'document')" />
    </div>
  </div>
</template>

<script>
import { TeamPosts } from "@/services/team-posts-service";
import Axios from "axios";
import VoiceDictationButton from "@/components/VoiceDictationButton.vue";
import { avatarUrl } from "@/utils/avatar";

export default {
  name: "PostComposer",
  components: { VoiceDictationButton },
  props: {
    userData: {
      type: Object,
      required: true,
    },
    teamId: {
      type: [Number, String],
      required: true,
    },
    clubId: {
      type: [Number, String],
      required: true,
    },
    teamName: {
      type: String,
      default: "",
    },
  },
  emits: ["post-created"],
  data() {
    return {
      isExpanded: false,
      content: "",
      attachments: [],
      isSubmitting: false,
      errorMessage: "",
      statusMessage: "",
    };
  },
  computed: {
    submitLabel() {
      if (!this.isSubmitting) return "Post";
      return this.statusMessage || "Posting...";
    },
    userAvatar() {
      if (this.userData.userImage) {
        return this.getImgUrl(this.userData.userImage);
      }
      return this.getImgUrl("default-image.png");
    },
    canPost() {
      return this.content.trim().length > 0 || this.attachments.length > 0;
    },
  },
  methods: {
    getImgUrl(imageName) {
      return avatarUrl(imageName);
    },

    expandComposer() {
      this.isExpanded = true;
      this.$nextTick(() => {
        this.$refs.contentInput?.focus();
      });
    },

    collapseComposer() {
      if (this.content.trim() || this.attachments.length > 0) {
        if (!confirm("Discard this post?")) return;
      }
      this.isExpanded = false;
      this.content = "";
      this.attachments = [];
    },

    autoResize(event) {
      this.errorMessage = "";
      const textarea = event.target;
      textarea.style.height = "auto";
      textarea.style.height = textarea.scrollHeight + "px";
    },

    // Turn a raw backend/network error into a clear, user-friendly message.
    friendlyError(error) {
      const status = error?.response?.status;
      const raw =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "";

      if (
        status === 413 ||
        /upload_max_filesize|MAX_FILE_SIZE|post_max_size|exceeds.*(file)?size|too large/i.test(raw)
      ) {
        return "That file is too large for the server to accept. Please try a shorter or more compressed video — or ask an admin to raise the server's upload limit.";
      }
      if (/partially uploaded|partial/i.test(raw)) {
        return "The upload was interrupted. Check your connection and try again.";
      }
      if (/YouTube not configured/i.test(raw)) {
        return "Video uploads aren't set up on the server yet. Please contact an admin.";
      }
      if (/access token|OAuth|re-authoriz/i.test(raw)) {
        return "The video service needs re-authorising. Please contact an admin.";
      }
      if (/Invalid video format/i.test(raw)) {
        return "That video format isn't supported. Allowed formats: MP4, MOV, AVI, WebM, WMV.";
      }
      if (error?.code === "ECONNABORTED" || /timeout/i.test(raw)) {
        return "The upload timed out — the video may be too large or your connection too slow. Try a smaller video.";
      }
      if (raw) return raw;
      return "Something went wrong creating your post. Please try again.";
    },

    triggerFileInput(type) {
      const inputRef = type + "Input";
      this.$refs[inputRef]?.click();
    },

    handleFileSelect(event, type) {
      const files = Array.from(event.target.files);
      files.forEach((file) => {
        // Validate file size (50MB max)
        if (file.size > 50 * 1024 * 1024) {
          this.errorMessage = `"${file.name}" is too large (${this.formatSize(
            file.size
          )}). The maximum size is 50MB.`;
          return;
        }

        const attachment = {
          file,
          type,
          preview: null,
          uploading: false,
          progress: 0,
          uploaded: false,
          uploadedData: null,
        };

        // Create preview
        if (type === "image" || type === "video") {
          attachment.preview = URL.createObjectURL(file);
        }

        this.attachments.push(attachment);
      });

      // Reset input
      event.target.value = "";
    },

    removeAttachment(index) {
      const attachment = this.attachments[index];
      if (attachment.preview) {
        URL.revokeObjectURL(attachment.preview);
      }
      this.attachments.splice(index, 1);
    },

    async uploadAttachment(attachment) {
      // Use YouTube for video uploads
      if (attachment.type === "video") {
        return this.uploadVideoToYouTube(attachment);
      }

      const formData = new FormData();
      formData.append("file", attachment.file);
      formData.append("type", attachment.type);

      let uploadUrl = "";
      if (window.location.href.indexOf("8080") > -1) {
        uploadUrl = "http://www.localhost:8888/back-end-endpoint-chat-upload.php";
      } else {
        uploadUrl = "/personal-training-BE/back-end-endpoint-chat-upload.php";
      }

      attachment.uploading = true;

      try {
        const response = await Axios.post(uploadUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            attachment.progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          },
        });

        if (response.data.success) {
          attachment.uploaded = true;
          attachment.uploadedData = response.data.file;
        } else {
          throw new Error(response.data.error || "Upload failed");
        }
      } catch (error) {
        console.error("Upload error:", error);
        throw error;
      } finally {
        attachment.uploading = false;
      }
    },

    async uploadVideoToYouTube(attachment) {
      const formData = new FormData();
      formData.append("video", attachment.file);
      formData.append("title", `${this.teamName} - ${new Date().toLocaleDateString("en-GB")}`);
      formData.append("description", `Posted by ${this.userData.name} via PTFC App`);
      formData.append("team_name", this.teamName);

      let uploadUrl = "";
      if (window.location.href.indexOf("8080") > -1) {
        uploadUrl = "http://www.localhost:8888/back-end-endpoint-youtube-upload.php";
      } else {
        uploadUrl = "/personal-training-BE/back-end-endpoint-youtube-upload.php";
      }

      attachment.uploading = true;

      try {
        const response = await Axios.post(uploadUrl, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 600000, // 10 minute timeout for large videos
          onUploadProgress: (progressEvent) => {
            attachment.progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
          },
        });

        if (response.data.success) {
          attachment.uploaded = true;
          // Store YouTube-specific data
          attachment.uploadedData = {
            fileName: response.data.data.video_id,
            path: response.data.data.embed_url,
            size: attachment.file.size,
            name: response.data.data.title,
            type: "video/youtube",
            thumbnail: response.data.data.thumbnail_url,
            youtubeId: response.data.data.video_id,
            youtubeUrl: response.data.data.youtube_url,
          };
        } else {
          throw new Error(response.data.message || "YouTube upload failed");
        }
      } catch (error) {
        console.error("YouTube upload error:", error);
        if (error.response?.data?.message) {
          throw new Error(error.response.data.message);
        }
        throw error;
      } finally {
        attachment.uploading = false;
      }
    },

    async submitPost() {
      if (!this.canPost || this.isSubmitting) return;

      this.isSubmitting = true;
      this.errorMessage = "";

      try {
        // Upload all attachments first
        const total = this.attachments.filter((a) => !a.uploaded).length;
        let done = 0;
        for (const attachment of this.attachments) {
          if (!attachment.uploaded) {
            done += 1;
            const noun =
              attachment.type === "video"
                ? "video"
                : attachment.type === "image"
                ? "photo"
                : "file";
            this.statusMessage =
              total > 1
                ? `Uploading ${noun} ${done}/${total}…`
                : `Uploading ${noun}…`;
            await this.uploadAttachment(attachment);
          }
        }
        this.statusMessage = "Posting…";

        // Prepare attachments data
        const attachmentsData = this.attachments
          .filter((a) => a.uploaded && a.uploadedData)
          .map((a) => ({
            type: a.type,
            fileName: a.uploadedData.fileName,
            filePath: a.uploadedData.path,
            fileSize: a.uploadedData.size,
            originalName: a.uploadedData.name,
            mimeType: a.uploadedData.type,
            thumbnail: a.uploadedData.thumbnail || null,
          }));

        // Create post
        const response = await TeamPosts.createPost({
          teamId: this.teamId,
          clubId: this.clubId,
          authorId: this.userData.id,
          authorName: this.userData.name,
          authorImage: this.userData.userImage,
          authorRole: this.userData.userType,
          content: this.content,
          attachments: attachmentsData,
          teamName: this.teamName,
        });

        if (response.data.success) {
          this.$emit("post-created", response.data.post);

          // Capture the post content BEFORE clearing the textarea — the
          // notification call below previously used `this.content` after
          // it had been reset, sending an empty body to the team.
          const notifyContent = this.content;

          // Reset form
          this.content = "";
          this.attachments.forEach((a) => {
            if (a.preview) URL.revokeObjectURL(a.preview);
          });
          this.attachments = [];
          this.isExpanded = false;

          // Trigger notification
          await TeamPosts.notifyTeam({
            teamId: this.teamId,
            authorId: this.userData.id,
            content: notifyContent,
            teamName: this.teamName,
          });
        } else {
          throw new Error(response.data.error || "Failed to create post");
        }
      } catch (error) {
        console.error("Error creating post:", error);
        this.errorMessage = this.friendlyError(error);
      } finally {
        this.isSubmitting = false;
        this.statusMessage = "";
      }
    },

    formatSize(bytes) {
      if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + "MB";
      if (bytes >= 1024) return Math.round(bytes / 1024) + "KB";
      return bytes + "B";
    },
  },
};
</script>

<style scoped>
.post-composer {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  margin-bottom: 16px;
}

.composer-error {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 16px 8px;
  padding: 10px 12px;
  background: #fce8e6;
  border: 1px solid #f5c2bd;
  border-radius: 8px;
  color: #b3261e;
  font-size: 14px;
  line-height: 1.4;
}

.composer-error svg {
  flex: 0 0 auto;
  margin-top: 1px;
}

.composer-error span {
  flex: 1;
}

.composer-error__close {
  background: none;
  border: 0;
  color: #b3261e;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}

.composer-collapsed {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
}

.user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
}

.placeholder-input {
  flex: 1;
  padding: 10px 16px;
  background: #f0f2f5;
  border-radius: 20px;
  color: #65676b;
  font-size: 17px;
}

.composer-expanded {
  padding: 16px;
}

.composer-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.user-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.user-name {
  font-weight: 600;
  color: #050505;
  font-size: 15px;
}

.user-role {
  font-size: 12px;
  color: #65676b;
  background: #e4e6eb;
  padding: 2px 8px;
  border-radius: 4px;
  width: fit-content;
  margin-top: 2px;
}

.close-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: #e4e6eb;
  border-radius: 50%;
  font-size: 20px;
  cursor: pointer;
  color: #65676b;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #d8dadf;
}

.composer-body {
  margin-bottom: 16px;
}

.content-input {
  width: 100%;
  min-height: 100px;
  border: none;
  resize: none;
  font-size: 24px;
  color: #1c1e21;
  outline: none;
}

.content-input::placeholder {
  color: #65676b;
}

.attachments-preview {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 8px;
  margin-top: 16px;
}

.attachment-item {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  background: #f0f2f5;
}

.preview-image img,
.preview-video video {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.preview-document {
  display: flex;
  align-items: center;
  padding: 16px;
  gap: 8px;
}

.doc-name {
  font-size: 13px;
  color: #050505;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.remove-attachment {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border: none;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: 50%;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: #e4e6eb;
}

.progress-bar {
  height: 100%;
  background: #1877f2;
  transition: width 0.2s ease;
}

.composer-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid #e4e6eb;
  border-radius: 8px;
  gap: 1rem;
}

.add-to-post {
  display: flex;
  align-items: center;
  gap: 12px;
}

.add-to-post span {
  font-size: 15px;
  font-weight: 600;
  color: #050505;
}

.attachment-buttons {
  display: flex;
  gap: 8px;
}

.attach-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px 16px;
  border: none;
  background: #f0f2f5;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  svg {
    flex-shrink: 0;
  }

  .btn-label {
    font-size: 12px;
    font-weight: 600;
    color: #65676b;
  }
}

.attach-btn:hover {
  background: #e4e6eb;
  transform: translateY(-2px);
}

.attach-btn.photo-btn:hover {
  background: #e8f5e9;
}

.attach-btn.video-btn:hover {
  background: #ffebee;
}

.attach-btn.doc-btn:hover {
  background: #e3f2fd;
}

.retention-notice {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 10px 4px 0;
  color: #65676b;
  font-size: 12px;
  line-height: 1.4;
}

.retention-notice svg {
  flex: 0 0 auto;
}

.post-btn {
  padding: 8px 32px;
  background: #1877f2;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
}

.post-btn:hover:not(:disabled) {
  background: #166fe5;
}

.post-btn:disabled {
  background: #e4e6eb;
  color: #bcc0c4;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .composer-footer {
    flex-direction: column;
    gap: 12px;
  }

  .add-to-post {
    width: 100%;
    flex-direction: column;
    gap: 12px;

    >span {
      font-size: 14px;
    }
  }

  .attachment-buttons {
    width: 100%;
    justify-content: space-between;
  }

  .attach-btn {
    flex: 1;
    padding: 10px 8px;

    svg {
      width: 28px;
      height: 28px;
    }

    .btn-label {
      font-size: 11px;
    }
  }

  .post-btn {
    width: 100%;
  }

  .content-input {
    font-size: 18px;
  }
}
</style>
